import { c as _c } from "react/compiler-runtime";
import { createElement, useReducer, useMemo, CSSProperties, ReactNode, Dispatch } from 'react';
import cx from 'classnames';
import { useDeepCompareEffect } from '@commutifi-fe/hooks';
import { helpEmail, HttpStatusCode } from '@commutifi-fe/constants';
import { Compass as ErrorCompass } from '@commutifi-fe/custom-icons/svgs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { Button } from '../../AppButton';
import { useLocaleReceiver } from '../../locales/LocaleReceiver';
import { colors } from '../../style/constants';
import Error403 from './error-403.png';
import './styles.scss';

// PNG imports are `string` in webpack but `StaticImageData` in Next.js — extract string for both
const error403Src = typeof Error403 === 'string' ? Error403 : (Error403 as {
  src: string;
}).src;
export interface GenericErrorProps {
  /**
   * Error object with status so we can infer the type of error and show the right
   * title icon, description, etc.
   */
  error?: unknown;
  /**
   * Type of the error. Page means we will display a background image and a button to
   * return home by default. Component won't have those by default but you can define actions
   * prop with a button if you want
   */
  type?: 'page' | 'component';
  /**
   * Override the error state icon with a custom icon
   */
  icon?: ReactNode;
  /**
   * When you need to hide the error image/icon set this boolean to true
   */
  hideErrorIcon?: boolean;
  /**
   * Overwrite the error title. You can set it to an empty string to hide it
   */
  title?: ReactNode;
  /**
   * Overwrite the text on the button
   */
  buttonText?: ReactNode;
  /**
   * Overwrite the error description. You can set it to an empty string to hide it
   */
  description?: ReactNode;
  /**
   * Define your own component(s) to have custom actions
   * (ex. instead of the default button that redirect the user you can define a button to reload the page)
   */
  actions?: ReactNode;
  /**
   * Element to use to build the lik on top of the button to redirect the user
   */
  linkElement?: string;
  /**
   * Redirect url
   */
  redirect?: string;
  /**
   * Style object to apply to the component
   */
  style?: CSSProperties;
  /**
   * Class name to apply to the component
   */
  className?: string;
  /**
   * Whether to use compact mode which display the error on limited space
   */
  compact?: boolean;
}
interface ErrorStateProps {
  img?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
}
enum TypeKeys {
  SetError = 'set_error',
}
interface ErrorAction {
  type: TypeKeys;
  payload: ErrorStateProps;
}
type ErrorConfigKeyType = 404 | 403 | 500 | 0;

/**
 * Link component for contacting support.
 *
 * This component is used to display the link to contact support. It will use the
 * default email address from the helpEmail constant.
 */
function ContactUsLink(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <a href={`mailto:${helpEmail}`} target="__blank">{children}</a>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

/**
 * Error description component.
 *
 * This component is used to display the error description. It will use the default
 * description if no description is provided.
 */
function ErrorDescription(t0) {
  const $ = _c(6);
  const {
    description,
    children
  } = t0;
  const intl = useLocaleReceiver("GenericError");
  let t1;
  if ($[0] !== description || $[1] !== intl) {
    t1 = description || intl.formatMessage({
      id: "description"
    }, {
      contactUsLink: <ContactUsLink key="error-contact-us">{intl.formatMessage({
          id: "contactUs"
        })}</ContactUsLink>
    });
    $[0] = description;
    $[1] = intl;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== children || $[4] !== t1) {
    t2 = <div className="app-generic-error-description"><span>{t1}{children}</span></div>;
    $[3] = children;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}

/**
 * Error icon component.
 * Compact icon will be limited in size by our stylesheet and if no icon is provided
 * we will use the default circle exclamation icon from FontAwesome.
 *
 * If it's not compact we simply use the original icon, if provided, or one of our known error image.
 */
function ErrorIcon(t0) {
  const $ = _c(6);
  const {
    icon,
    compact,
    errorImg
  } = t0;
  if (compact) {
    let t1;
    if ($[0] !== icon) {
      t1 = icon || <FontAwesomeIcon icon={faCircleExclamation} color={colors.feedbackDestructive400} size="1x" />;
      $[0] = icon;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <span className="app-generic-error-icon">{t1}</span>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  if (icon) {
    let t1;
    if ($[4] !== icon) {
      t1 = <div>{icon}</div>;
      $[4] = icon;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    return t1;
  }
  return errorImg;
}

/**
 * Error actions component
 */
function ErrorActions(t0) {
  const $ = _c(10);
  const {
    type,
    actions,
    linkElement: t1,
    redirect: t2,
    buttonText
  } = t0;
  const linkElement = t1 === undefined ? "a" : t1;
  const redirect = t2 === undefined ? "/" : t2;
  const intl = useLocaleReceiver("GenericError");
  if (actions) {
    return actions;
  }
  if (type === "page") {
    let t3;
    if ($[0] !== buttonText || $[1] !== intl || $[2] !== linkElement || $[3] !== redirect) {
      let t4;
      if ($[5] !== buttonText || $[6] !== intl) {
        t4 = buttonText || intl.formatMessage({
          id: "goBackHomeButton"
        });
        $[5] = buttonText;
        $[6] = intl;
        $[7] = t4;
      } else {
        t4 = $[7];
      }
      let t5;
      if ($[8] !== t4) {
        t5 = <Button size="middle" type="primary" className="u-margin-top--large"><span>{t4}</span></Button>;
        $[8] = t4;
        $[9] = t5;
      } else {
        t5 = $[9];
      }
      t3 = createElement(linkElement, {
        to: redirect,
        href: redirect
      }, t5);
      $[0] = buttonText;
      $[1] = intl;
      $[2] = linkElement;
      $[3] = redirect;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  return null;
}

/**
 * Error content component
 */
function ErrorContent(t0) {
  const $ = _c(17);
  const {
    title,
    description,
    compact
  } = t0;
  const t1 = !compact && "u-margin-top--large";
  let t2;
  if ($[0] !== t1) {
    t2 = cx("u-flex-column", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== title) {
    t3 = title ? <span className="app-generic-error-text">{title}</span> : null;
    $[2] = title;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== compact || $[5] !== description) {
    t4 = compact && description ? <span className="app-generic-error-description--compact">{description}</span> : null;
    $[4] = compact;
    $[5] = description;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    t5 = <div className={t2}>{t3}{t4}</div>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== compact || $[12] !== description) {
    t6 = !compact && description ? <span className="app-generic-error-description">{description}</span> : null;
    $[11] = compact;
    $[12] = description;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
function errorReducer(state: ErrorStateProps, action: ErrorAction): ErrorStateProps {
  const {
    type,
    payload
  } = action;
  let changes;
  switch (type) {
    case TypeKeys.SetError:
      {
        const {
          img,
          title,
          description
        } = payload;
        changes = {
          img,
          title,
          description
        };
        break;
      }
    default:
      {
        throw new Error(`Unhandled errorReducer action type: ${type as any}`);
      }
  }
  return {
    ...state,
    ...changes
  };
}
export const useError = (error, t0) => {
  const $ = _c(48);
  const {
    title,
    description
  } = t0;
  const intl = useLocaleReceiver("GenericError");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [state, dispatch] = useReducer(errorReducer, t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ErrorCompass height={80} />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== intl) {
    t3 = intl.formatMessage({
      id: "error404Title"
    });
    $[2] = intl;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== intl) {
    const t5 = intl.formatMessage({
      id: "contactUs"
    });
    let t6;
    if ($[6] !== t5) {
      t6 = <ContactUsLink>{t5}</ContactUsLink>;
      $[6] = t5;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    t4 = intl.formatMessage({
      id: "error404Description"
    }, {
      link: t6
    });
    $[4] = intl;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[8] !== intl) {
    t5 = intl.formatMessage({
      id: "refresh"
    });
    $[8] = intl;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = <ErrorDescription description={t4}> {t5}</ErrorDescription>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t3 || $[14] !== t6) {
    t7 = {
      img: t2,
      title: t3,
      description: t6
    };
    $[13] = t3;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <img src={error403Src} className={cx("app-generic-error-icon", "u-margin-bottom--medium")} alt="generic error" />;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== intl) {
    t9 = intl.formatMessage({
      id: "error403Title"
    });
    $[17] = intl;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t9) {
    t10 = {
      img: t8,
      title: t9,
      description: ""
    };
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <ErrorCompass height={80} />;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== intl) {
    t12 = intl.formatMessage({
      id: "error500Title"
    });
    $[22] = intl;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <ErrorDescription />;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t12) {
    t14 = {
      img: t11,
      title: t12,
      description: t13
    };
    $[25] = t12;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <ErrorCompass height={80} />;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== intl) {
    t16 = intl.formatMessage({
      id: "errorSorry"
    });
    $[28] = intl;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <ErrorDescription />;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t16) {
    t18 = {
      img: t15,
      title: t16,
      description: t17
    };
    $[31] = t16;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t10 || $[34] !== t14 || $[35] !== t18 || $[36] !== t7) {
    t19 = {
      [HttpStatusCode.NotFound]: t7,
      [HttpStatusCode.Forbidden]: t10,
      [HttpStatusCode.InternalServerError]: t14,
      [HttpStatusCode.Unknown]: t18
    };
    $[33] = t10;
    $[34] = t14;
    $[35] = t18;
    $[36] = t7;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  const errorTypeConfig = t19;
  let t20;
  if ($[38] !== description || $[39] !== error || $[40] !== errorTypeConfig || $[41] !== title) {
    t20 = () => {
      const errorStatus = error && typeof error === "object" ? error.response?.status || error.status || error.statusCode || error.httpCode : undefined;
      const errorType = Object.prototype.hasOwnProperty.call(errorTypeConfig, errorStatus) ? errorStatus : HttpStatusCode.Unknown;
      const errorConfig = errorTypeConfig[errorType];
      dispatch({
        type: TypeKeys.SetError,
        payload: {
          img: errorConfig.img,
          title: title || title === "" ? title : errorConfig.title,
          description: description || errorConfig.description
        }
      });
    };
    $[38] = description;
    $[39] = error;
    $[40] = errorTypeConfig;
    $[41] = title;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== error || $[44] !== title) {
    t21 = [error, title];
    $[43] = error;
    $[44] = title;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  useDeepCompareEffect(t20, t21);
  let t22;
  if ($[46] !== state) {
    t22 = [state, dispatch];
    $[46] = state;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  return t22;
};
export function GenericError(t0) {
  const $ = _c(30);
  const {
    error,
    type: t1,
    hideErrorIcon: t2,
    icon,
    title,
    buttonText,
    description,
    actions,
    linkElement: t3,
    redirect: t4,
    style,
    className,
    compact
  } = t0;
  const type = t1 === undefined ? "component" : t1;
  const hideErrorIcon = t2 === undefined ? false : t2;
  const linkElement = t3 === undefined ? "a" : t3;
  const redirect = t4 === undefined ? "/" : t4;
  let t5;
  if ($[0] !== description || $[1] !== title) {
    t5 = {
      title,
      description
    };
    $[0] = description;
    $[1] = title;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const [errorState] = useError(error, t5);
  const t6 = compact && "app-generic-error--compact";
  const t7 = type === "page" && "app-generic-error--full-page";
  let t8;
  if ($[3] !== className || $[4] !== t6 || $[5] !== t7) {
    t8 = cx("app-generic-error", t6, t7, className);
    $[3] = className;
    $[4] = t6;
    $[5] = t7;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== compact || $[8] !== errorState.img || $[9] !== hideErrorIcon || $[10] !== icon) {
    t9 = !hideErrorIcon ? <ErrorIcon icon={icon} compact={compact} errorImg={errorState.img} /> : null;
    $[7] = compact;
    $[8] = errorState.img;
    $[9] = hideErrorIcon;
    $[10] = icon;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== compact || $[13] !== errorState.description || $[14] !== errorState.title) {
    t10 = <ErrorContent title={errorState.title} description={errorState.description} compact={compact} />;
    $[12] = compact;
    $[13] = errorState.description;
    $[14] = errorState.title;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t9) {
    t11 = <div className="app-generic-error-header">{t9}{t10}</div>;
    $[16] = t10;
    $[17] = t9;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== actions || $[20] !== buttonText || $[21] !== linkElement || $[22] !== redirect || $[23] !== type) {
    t12 = <ErrorActions type={type} actions={actions} linkElement={linkElement} redirect={redirect} buttonText={buttonText} />;
    $[19] = actions;
    $[20] = buttonText;
    $[21] = linkElement;
    $[22] = redirect;
    $[23] = type;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== style || $[26] !== t11 || $[27] !== t12 || $[28] !== t8) {
    t13 = <div data-testid="generic-error" className={t8} style={style}>{t11}{t12}</div>;
    $[25] = style;
    $[26] = t11;
    $[27] = t12;
    $[28] = t8;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
}